import type {
  CreateStockLineRequest,
  PartOrder,
  PartOrderLine,
  PartOrderLineStatus,
  PlaceOrderRequest,
  ReceiveLineRequest,
  SendFromQuoteRequest,
  SendToBasketResponse,
  UpdatePartOrderLineRequest,
  UpdatePartOrderRequest,
} from '@techgitter/contracts';
import { readApiError } from './readApiError';

const LINES_URL = '/api/part_order_lines';
const ORDERS_URL = '/api/part_orders';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      detail = `: ${(await response.text()).slice(0, 200)}`;
    } catch {
      // ignore
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export interface FetchPartOrderLinesParams {
  status?: PartOrderLineStatus | 'all';
  vendorId?: string;
  workitemId?: string;
  sort?: string;
}

// Shared in-flight promises so concurrent callers with identical params reuse
// a single network request (StrictMode double-mount, Header + Dashboard hooks).
const inFlightLines = new Map<string, Promise<PartOrderLine[]>>();

export async function fetchPartOrderLines(
  params: FetchPartOrderLinesParams = {},
  signal?: AbortSignal,
): Promise<PartOrderLine[]> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const key = JSON.stringify({
    status: params.status ?? null,
    vendorId: params.vendorId ?? null,
    workitemId: params.workitemId ?? null,
    sort: params.sort ?? null,
  });

  const existing = inFlightLines.get(key);
  if (existing) {
    return new Promise<PartOrderLine[]>((resolve, reject) => {
      const onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
      signal?.addEventListener('abort', onAbort, { once: true });
      existing.then(
        v => { signal?.removeEventListener('abort', onAbort); resolve(v); },
        e => { signal?.removeEventListener('abort', onAbort); reject(e); },
      );
    });
  }

  const promise = (async () => {
    try {
      const search = new URLSearchParams();
      if (params.status && params.status !== 'all') search.set('status', params.status);
      if (params.vendorId) search.set('vendorId', params.vendorId);
      if (params.workitemId) search.set('workitemId', params.workitemId);
      if (params.sort) search.set('sort', params.sort);
      const qs = search.toString();
      const url = qs ? `${LINES_URL}?${qs}` : LINES_URL;
      const response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
      const data = await readJson<{ items: PartOrderLine[] }>(response, 'fetchPartOrderLines');
      return data.items ?? [];
    } finally {
      inFlightLines.delete(key);
    }
  })();
  inFlightLines.set(key, promise);

  return fetchPartOrderLines(params, signal);
}

export async function fetchPartOrder(id: string, signal?: AbortSignal): Promise<PartOrder> {
  const response = await fetch(`${ORDERS_URL}/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  return readJson<PartOrder>(response, 'fetchPartOrder');
}

export async function getNextPoNumber(signal?: AbortSignal): Promise<string> {
  const response = await fetch(`${ORDERS_URL}/next_po_number`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await readJson<{ poNumber: string }>(response, 'getNextPoNumber');
  return data.poNumber ?? '';
}

export async function createStockLine(
  request: CreateStockLineRequest,
  signal?: AbortSignal,
): Promise<PartOrderLine> {
  const response = await fetch(LINES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<PartOrderLine>(response, 'createStockLine');
}

export async function sendFromQuote(
  body: SendFromQuoteRequest,
  signal?: AbortSignal,
): Promise<SendToBasketResponse> {
  const response = await fetch(`${ORDERS_URL}/send_from_quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  return readJson<SendToBasketResponse>(response, 'sendFromQuote');
}

export async function placeOrder(
  request: PlaceOrderRequest,
  signal?: AbortSignal,
): Promise<PartOrder> {
  const response = await fetch(ORDERS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<PartOrder>(response, 'placeOrder');
}

export async function updatePartOrder(
  id: string,
  patch: UpdatePartOrderRequest,
  signal?: AbortSignal,
): Promise<PartOrder> {
  const response = await fetch(`${ORDERS_URL}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(patch),
    signal,
  });
  return readJson<PartOrder>(response, 'updatePartOrder');
}

export async function receiveLine(
  lineId: string,
  request: ReceiveLineRequest,
  signal?: AbortSignal,
): Promise<PartOrderLine> {
  const response = await fetch(`${LINES_URL}/${encodeURIComponent(lineId)}/receive`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  return readJson<PartOrderLine>(response, 'receiveLine');
}

export interface ReprintLabelResult {
  /** True when at least one AutoPrint rule actually published a print job. */
  published: boolean;
}

/**
 * Reprint the part-receipt customer label for an already-received line. Re-runs
 * the same label AutoPrint the receipt fired (rendered server-side from the
 * line's customer + quote QR), forcing `copies` (default 1 — the lost/damaged
 * label case). Returns whether a print job actually published.
 */
export async function reprintReceiptLabel(
  lineId: string,
  copies = 1,
  signal?: AbortSignal,
): Promise<ReprintLabelResult> {
  const response = await fetch(`${LINES_URL}/${encodeURIComponent(lineId)}/reprint_label`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ copies }),
    signal,
  });
  const data = await readJson<{ autoPrint?: Array<{ published?: boolean }> | null }>(
    response,
    'reprintReceiptLabel',
  );
  const published = Array.isArray(data.autoPrint) && data.autoPrint.some((r) => !!r?.published);
  return { published };
}

/**
 * Pre-PO lines are hard-deleted server-side and the response is 204; downstream
 * `ordered`/`partially_received`/`received` lines flip to `cancelled` and the
 * updated line is returned. Callers should treat `null` as "row gone".
 */
/**
 * Edit a line still in the basket — supplier, SKU, description, buy price (ex
 * GST), product URL. Only the keys sent change. The API refuses a line that has
 * left the basket (409); its message is surfaced as-is, so it reads in the panel.
 */
export async function updatePartOrderLine(
  lineId: string,
  body: UpdatePartOrderLineRequest,
  signal?: AbortSignal,
): Promise<PartOrderLine> {
  const response = await fetch(`${LINES_URL}/${encodeURIComponent(lineId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.ok) throw new Error(await readApiError(response, "Couldn't update the line"));
  return (await response.json()) as PartOrderLine;
}

export async function cancelLine(
  lineId: string,
  signal?: AbortSignal,
): Promise<PartOrderLine | null> {
  const response = await fetch(`${LINES_URL}/${encodeURIComponent(lineId)}/cancel`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({}),
    signal,
  });
  if (response.status === 204) return null;
  return readJson<PartOrderLine>(response, 'cancelLine');
}
